// useProfilePrefs — the self-mutating theme + language controls that live in
// the account menu (both are personal preferences, so they live in the avatar
// menu, not as standalone top-bar controls), and the person's time zone (the
// profile tab's field and the drift strip's "Use …" — ADR-082 §3).
//
// Theme is a 3-state preference (light / dark / system) — `system` follows the
// OS via prefers-color-scheme. The control writes the chosen mode directly so
// `system` stays reachable (a 2-state flip could never return to it).
//
// One PATCH /auth/me/profile endpoint for every self-mutable field; on success
// invalidate ['me'] + broadcast `me-invalidated` (Layer 3) so ThemeProvider +
// useLanguageSync re-apply in ~0s across sibling tabs (FRONTEND-PATTERNS
// §Mutations — self-mutating mutations invalidate ['me']; SPA-clean, no reload).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { apiClient } from '../lib/apiClient.js';
import { broadcastAuth } from '../lib/authBroadcast.js';
import { useMe, ME_QUERY_KEY } from './useMe.js';
import { useRefusalToast } from '../components/patterns/forms/useQuickSave.js';

type ThemeMode = 'light' | 'dark' | 'system';
// Derived from the wire schema (ADR-009 — never hand-author a type that can
// drift from the contract). Tracks the `language` enum, so adding a locale
// there flows here automatically.
type Language = MeResponse['user']['language'];

// Module-level selectors — referentially stable per the select-per-call-site
// doctrine (FRONTEND-PATTERNS §State).
const selectThemeMode = (m: MeResponse) => m.user.theme_mode;
const selectLanguage = (m: MeResponse) => m.user.language;
const selectTimezone = (m: MeResponse) => m.user.timezone_iana;

export interface ProfilePrefs {
  themeMode: ThemeMode | undefined;
  language: Language | undefined;
  /** The person's zone (`users.timezone_iana`) — an IANA name. */
  timezone: string | undefined;
  isPending: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  setLanguage: (lang: Language) => void;
  setTimezone: (zone: string) => void;
}

export function useProfilePrefs(): ProfilePrefs {
  const queryClient = useQueryClient();
  const refused = useRefusalToast('profile-prefs');
  const { data: themeMode } = useMe(selectThemeMode);
  const { data: language } = useMe(selectLanguage);
  const { data: timezone } = useMe(selectTimezone);

  const mutation = useMutation({
    mutationFn: (patch: { theme_mode?: ThemeMode; language?: Language; timezone_iana?: string }) =>
      apiClient.patch('/auth/me/profile', patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      broadcastAuth({ type: 'me-invalidated' });
    },
    // The controls are bound to `/me`, so a failed save would quietly show the old value: say it, once, in its own
    // words (silent on a busy answer — the door said it).
    onError: refused,
  });

  return {
    themeMode,
    language,
    timezone,
    isPending: mutation.isPending,
    setThemeMode: (mode) => {
      if (mode !== themeMode) mutation.mutate({ theme_mode: mode });
    },
    setLanguage: (lang) => {
      if (lang !== language) mutation.mutate({ language: lang });
    },
    setTimezone: (zone) => {
      if (zone !== timezone) mutation.mutate({ timezone_iana: zone });
    },
  };
}
